import { TONE_CLASSES, TONE_DOT } from '../../lib/domain';
import { Icon, TONE_ICON } from './Icon';

/**
 * Status, in three densities.
 *
 *   <Badge>          label in a tinted capsule — tables and headers
 *   <StatusDot>      a dot plus a label — dense lists
 *   <Pill>           neutral chip for counts and metadata
 *
 * All three take a `tone` from the domain vocabulary rather than a colour, so
 * "what colour is degraded" is answered in one file and nowhere else. A status
 * badge always carries its label; hue never has to do the work alone.
 */

export function Badge({ tone = 'neutral', icon, children, size = 'md', className = '' }) {
  const glyph = icon === undefined ? TONE_ICON[tone] : icon;

  return (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-full border font-medium whitespace-nowrap',
        size === 'sm' ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-0.5 text-[12px]',
        TONE_CLASSES[tone] || TONE_CLASSES.neutral,
        className,
      ].join(' ')}
    >
      {glyph && <Icon name={glyph} size="xs" strokeWidth={2} />}
      {children}
    </span>
  );
}

export function StatusDot({ tone = 'neutral', label, pulse = false, className = '' }) {
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap ${className}`}>
      <span className="relative flex h-2 w-2 shrink-0">
        {pulse && (
          <span
            className={`absolute inline-flex h-full w-full rounded-full opacity-60 animate-live ${
              TONE_DOT[tone] || TONE_DOT.neutral
            }`}
          />
        )}
        <span
          className={`relative inline-flex h-2 w-2 rounded-full ${
            TONE_DOT[tone] || TONE_DOT.neutral
          }`}
        />
      </span>
      {label && <span className="text-[13px] text-ink-2">{label}</span>}
    </span>
  );
}

export function Pill({ children, className = '' }) {
  return (
    <span
      className={`inline-flex items-center rounded-md bg-surface-2 px-1.5 py-0.5 text-[11.5px] font-medium text-ink-3 tabular-nums ${className}`}
    >
      {children}
    </span>
  );
}

/**
 * The health meter. The fill carries severity; the track is a lighter step of
 * the same idea, so state reads across the whole bar rather than only where
 * the fill happens to end.
 */
export function HealthMeter({ score, tone = 'neutral', label, className = '' }) {
  const pct = score === null || score === undefined ? 0 : Math.max(0, Math.min(100, score));
  const fill = {
    good: 'bg-good',
    warning: 'bg-warning',
    serious: 'bg-serious',
    critical: 'bg-critical',
    neutral: 'bg-ink-3',
  }[tone];

  return (
    <div className={`min-w-0 ${className}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[12px] text-ink-3">{label}</span>
        <span className="text-[13px] font-semibold text-ink tabular-nums">
          {score === null || score === undefined ? '—' : score}
        </span>
      </div>
      <div
        className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-surface-3"
        role="meter"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div
          className={`h-full rounded-full transition-[width] duration-500 ${fill}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
